import { db, LEGACY_TENANT_ID } from "@/lib/firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  where,
} from "firebase/firestore";

export interface Reward {
  id: string; // Firestore document ID
  name: string;
  pointsRequired: number;
  iconName?: string;
  // Potentially: createdAt, createdBy for tracking
  tenantId?: string;
}

// Type for creating a new reward, ID is auto-generated by Firestore
export type NewReward = Omit<Reward, "id">;

const REWARDS_COLLECTION = "rewards";

/**
 * Fetches all rewards from Firestore, ordered by pointsRequired.
 */
export async function getRewards(tenantId: string = LEGACY_TENANT_ID): Promise<Reward[]> {
  try {
    const rewardsRef = collection(db, REWARDS_COLLECTION);
    let q;

    if (tenantId === LEGACY_TENANT_ID) {
      q = query(rewardsRef, orderBy("pointsRequired"));
    } else {
      q = query(rewardsRef, where("tenantId", "==", tenantId), orderBy("pointsRequired"));
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(
      (doc) => {
        const data = doc.data() as any;
        return { id: doc.id, ...data, tenantId: data.tenantId || LEGACY_TENANT_ID } as Reward;
      }
    ).filter(reward => {
      if (tenantId === LEGACY_TENANT_ID) {
        return !reward.tenantId || reward.tenantId === LEGACY_TENANT_ID;
      }
      return reward.tenantId === tenantId;
    });
  } catch (error) {
    console.error("Error fetching rewards:", error);
    throw error; // Re-throw to allow caller to handle
  }
}

/**
 * Adds a new reward to Firestore.
 */
export async function addReward(rewardData: NewReward): Promise<Reward> {
  try {
    if (rewardData.pointsRequired < 0) {
      throw new Error("Points required cannot be negative.");
    }

    const dataToSave = {
      ...rewardData,
      tenantId: rewardData.tenantId || LEGACY_TENANT_ID
    };

    const docRef = await addDoc(collection(db, REWARDS_COLLECTION), dataToSave);
    return { id: docRef.id, ...dataToSave };
  } catch (error) {
    console.error("Error adding reward:", error);
    throw error;
  }
}

/**
 * Updates an existing reward in Firestore.
 */
export async function updateReward(
  rewardId: string,
  rewardData: Partial<NewReward> // Allow partial updates
): Promise<void> {
  try {
    if (rewardData.pointsRequired !== undefined && rewardData.pointsRequired < 0) {
      throw new Error("Points required cannot be negative.");
    }
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    await updateDoc(rewardRef, rewardData);
  } catch (error) {
    console.error("Error updating reward:", error);
    throw error;
  }
}

/**
 * Deletes a reward from Firestore.
 */
export async function deleteReward(rewardId: string): Promise<void> {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    await deleteDoc(rewardRef);
  } catch (error) {
    console.error("Error deleting reward:", error);
    throw error;
  }
} 
